<template>
  <div class="fts-form-designer fts-element-designer">
    <!-- element design content -->
    <div class="form-designer-content element-designer-content fts-form-group">
      <fts-form-layouter
        :modules="modules"
        :form="form"
        :activity-id="activityId"
        :activity-design="form"
        :document="document"
        :information-source="information"
        :shortcut-options="shortcutOptions"
        :shortcut-elements="shortcutElements"
        :shortcut-selected-options="classificationInformation"
        :item-group="'row'"
        @added="elementAdded"
      />
    </div>

    <div class="form-designer-footer" v-if="!document">
      <div class="design-modal-form-group" v-if="!disabledDelete">
        <fts-btn color="danger" sm outline text-transform-init @click="deleteForm">Remove Form</fts-btn>
      </div>

      <fts-toggle-switch
        v-model="formAutomation"
        mat-class
        :size="16"
        class="design-modal-toggle-switch"
      >
        <div slot="label" class="design-modal-toggle-switch__label">Automation</div>
      </fts-toggle-switch>

      <div class="automation-design-content">
        <fts-automation-designer
          ref="automationDesigner"
          :module-id="moduleId"
          :activity-id="activityId"
          :save-button="automationSaveButton"
          :data="form.automation"
          :is-form="true"
          :form="form"
          @save="$emit('save')"
          v-if="form.automation"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import draggable from "vuedraggable";
import VueJsonPretty from "vue-json-pretty";
import wysiwyg from "../vue-wysiwyg/src/editor/index";
Vue.use(wysiwyg, {});
import pdf from "vue-pdf/src/vuePdfNoSss";

import { authorizationStore } from "../store/authorizationStore";
import commons from "../commons/commons";
import formDesign from '../commons/formDesign';

import FTSDesignElement from "./FTSDesignElement";
import FTSDesignToolItem from "./FTSDesignToolItem";
import FTSForm from "./FTSForm";
import FTSFormLayouter from "../design/FTSFormLayouter";
import FTSBtn from "./FTSBtn";
import FTSAutomationDesigner from "../design/automation/FTSAutomationDesigner.vue";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";

export default {
  components: {
    draggable,
    VueJsonPretty,
    pdf,
    "fts-design-element": FTSDesignElement,
    "fts-design-tool-item": FTSDesignToolItem,
    "fts-form": FTSForm,
    "fts-btn": FTSBtn,
    "fts-automation-designer": FTSAutomationDesigner,
    "fts-toggle-switch": FTSToggleSwitch
  },
  props: {
    modules: Array,
    document: Boolean,
    activityId: String,
    form: {
      type: Object,
      default: () => ({})
    },
    isCapableAddInformation: {
      type: Boolean,
      default: true
    },
    disabledDelete: Boolean,
    automationSaveButton: Boolean
  },
  computed: {
    moduleId() {
      return this.$store.state.vectorId.toString();
    },
    beta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    classificationInformation() {
      return commons.classificationInformation(
        this.form.rows,
        this.information
      );
    },
    shortcutOptions() {
      return [...this.information].filter(
        x => !this.classificationInformation.some(y => y.id === x.id)
      );
    },
    prefix() {
      if (this.document) {
        return [
          formDesign.Element.Content,
          formDesign.Element.Image,
          formDesign.Element.Space,
          {
            _name: "Section",
            input: "section",
            type: "section",
            design: { rows: [] }
          },
          {
            _name: "Column",
            input: "section",
            type: "column",
            design: { rows: [] }
          }
        ];
      }
      return [
        ...formDesign.elements,
        {
          _name: "Section",
          input: "section",
          type: "section",
          design: { rows: [] }
        },
        {
          _name: "Collapse",
          input: "section",
          type: "collapse",
          design: { rows: [] }
        },
        {
          _name: "Tab",
          input: "section",
          type: "tab",
          design: { rows: [] },
          tabs: [
            {
              id: commons.nanoID(),
              header: "(new tab)",
              design: { rows: [] }
            }
          ]
        },
        {
          _name: "Column",
          input: "section",
          type: "column",
          design: { rows: [] }
        }
      ];
    },
    shortcutElements() {
      return [...this.prefix.filter(x => (this.beta && x.beta) || !x.beta)];
    },
    formAutomation: {
      get() {
        return Boolean(this.form && this.form.automation);
      },
      set(val) {
        if (val) {
          this.setupAutomation();
        } else {
          this.$set(this.form, "automation", null);
        }
      }
    }
  },
  methods: {
    setupAutomation() {
      if (!this.form.automation) {
        Vue.set(this.form, "automation", {
          testLocusId: null,
          testLocus: null,
          testLocusDetailId: null,
          testLocusDetail: null
        });
        this.form.automation.blocks = {
          1: {
            id: 1,
            name: "start",
            data: {
              id: "start",
              variables: []
            },
            class: "start",
            html: "start",
            typenode: "vue",
            outputs: {
              output_1: {
                connections: []
              }
            },
            inputs: {},
            pos_x: 200,
            pos_y: 50
          }
        };
      }

      this.form.automation.blocks[1].data.variables = [
        {
          id: "form",
          fieldName: "form",
          name: "Form",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId,
          activityId: this.activityId
        },
        {
          id: "entry",
          fieldName: "entry",
          name: "Entry",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId
        },
        {
          id: "initial",
          fieldName: "initial",
          name: "Initial",
          nodeId: 1,
          type: 1
        },
        {
          id: "query",
          fieldName: "query",
          name: "Query",
          nodeId: 1,
          type: 37
        },
        {
          id: "interaction",
          fieldName: "interaction",
          name: "Interaction",
          nodeId: 1,
          type: 37
        }
      ];

      let vm = this;
      this.$nextTick(()=>{ 
        vm.$refs.automationDesigner.loadDiagram();
      });
    },
    deleteForm() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: "Remove form?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Remove",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$emit("form:delete");
            }
          }
        ]
      });
    },
    elementAdded(element) {
      if (element.custom) {
        if (!element.id) element.id = commons.nanoID();
      } else if (
        !element.id &&
        this.prefix.some(x => x.input === element.input)
      ) {
        this.$set(element, "id", commons.nanoID());
      }
    }
  },
  beforeCreate: function() {
    Vue.component("fts-form-layouter", FTSFormLayouter);
    Vue.component("fts-design-element", FTSDesignElement);
    Vue.component("fts-form", FTSForm);
  },
  // watch: {
  //   formAutomation: function(val) {
  //     if (val) {
  //       this.$refs.automationDesigner.loadDiagram();
  //     }
  //   }
  // },
  mounted() {
    if (this.form.automation) {
      this.$set(this.form.automation.blocks[1].data, "variables", [
        {
          id: "form",
          fieldName: "form",
          name: "Form",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId,
          activityId: this.activityId
        },
        {
          id: "entry",
          fieldName: "entry",
          name: "Entry",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId
        },
        {
          id: "initial",
          fieldName: "initial",
          name: "Initial",
          nodeId: 1,
          type: 1
        },
        {
          id: "query",
          fieldName: "query",
          name: "Query",
          nodeId: 1,
          type: 37
        },
        {
          id: "event",
          fieldName: "event",
          name: "Event",
          nodeId: 1,
          type: 37
        },
        {
          id: "interaction",
          fieldName: "interaction",
          name: "Interaction",
          nodeId: 1,
          type: 37
        }
      ]);

      this.$refs.automationDesigner.loadDiagram();

      // window.eventBus.$emit("refreshAutomationBlocks");
    }
  }
};
</script>
<style lang="scss">
@import "~vue-wysiwyg/dist/vueWysiwyg.css";
@import "./src/assets/scss/vars.scss";

.fts-form-designer {
  .form-designer-footer {
    padding-top: 1.5rem;
    border-top: 1px solid var(--fts-border);
  }
  .automation-design-content {
    margin-top: 1rem;
  }
}
</style>
